import type { VoidFunctionComponent } from 'react';
import { SubMenu, MenuHeader, MenuGroup } from '../../../generic/Menu';
import TeamMenuItem, { StaticTeamMenuItem } from './TeamMenuItem';
import type { TeamGroup } from '../../../../../shared/types/routes/teamGroups';
import styles from './TeamSelectionMenu.module.less';

type Props = {
  teamGroup: TeamGroup;
  isTeamSelected: (teamId: number) => boolean;
  selectTeam: (teamId: number) => void;
};

/**
 * Renders a menu item for a single team group.
 * If the team group has no teams, it renders a regular team menu item.
 * If the team group has teams, it renders a submenu with the teams,
 * allowing the user to select both the team group and the teams within it.
 */
const TeamGroupMenuItem: VoidFunctionComponent<Props> = ({ teamGroup, isTeamSelected, selectTeam }) => {
  //  Just render a regular TeamMenuItem if there are no teams in the team group
  if (teamGroup.teams.length === 0) {
    return (
      <TeamMenuItem
        key={teamGroup.id}
        name={teamGroup.name}
        isSelected={isTeamSelected(teamGroup.id)}
        onClick={() => selectTeam(teamGroup.id)}
      />
    );
  }

  return (
    <SubMenu
      key={teamGroup.id}
      label={(
        <StaticTeamMenuItem
          name={teamGroup.name}
          isSelected={false}
          hasSubMenu
        />
      )}
      setDownOverflow
      overflow="auto"
      position="auto"
      align="start"
      gap={8}
      //  This refers to the props passed to the root MenuItem component for this submenu
      itemProps={{
        style: {
          //  Because the SubMenu will apply its own padding, we want it to look more like a regular menu item
          paddingTop: 0,
          paddingBottom: 0,
          paddingLeft: 0,
          paddingRight: '16px',
        },
      }}
    >
      <MenuGroup className={styles.menuGroup}>
        <MenuHeader>{teamGroup.name}</MenuHeader>
        <TeamMenuItem
          name={`All projects in ${teamGroup.name}`}
          isSelected={isTeamSelected(teamGroup.id)}
          onClick={() => selectTeam(teamGroup.id)}
          shouldTruncate={false}
        />
        {teamGroup.teams.map((team) => (
          <TeamMenuItem
            key={team.id}
            name={team.name}
            isSelected={isTeamSelected(team.id)}
            onClick={() => selectTeam(team.id)}
            count={team.numProjects}
          />
        ))}
      </MenuGroup>
    </SubMenu>
  );
};

export default TeamGroupMenuItem;
